import React, { useState, useEffect } from 'react';
import { FiX, FiInfo, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActiveAnnouncements();
  }, []);

  const fetchActiveAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/announcements/active', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAnnouncements(response.data.announcements);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/announcements/${announcementId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from local state
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      toast.success('Announcement dismissed');
    } catch (error) {
      console.error('Error dismissing announcement:', error);
      toast.error('Failed to dismiss announcement');
    }
  };

  const getAnnouncementStyles = (type) => {
    const baseStyles = "relative overflow-hidden backdrop-blur-md border shadow-lg";
    
    switch (type) {
      case 'error':
        return `${baseStyles} bg-red-500/10 border-red-500/30 text-red-900 dark:text-red-100`;
      case 'warning':
        return `${baseStyles} bg-yellow-500/10 border-yellow-500/30 text-yellow-900 dark:text-yellow-100`;
      default: // info
        return `${baseStyles} bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-100`;
    }
  };

  const getAnnouncementIcon = (type) => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    
    switch (type) {
      case 'error':
        return <FiAlertCircle className={`${iconClass} text-red-500`} />;
      case 'warning':
        return <FiAlertTriangle className={`${iconClass} text-yellow-500`} />;
      default: // info
        return <FiInfo className={`${iconClass} text-blue-500`} />;
    }
  };

  if (loading || announcements.length === 0) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50 space-y-1">
      {announcements.map((announcement) => (
        <div
          key={announcement.id}
          className={getAnnouncementStyles(announcement.type)}
        >
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
          
          <div className="relative px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {getAnnouncementIcon(announcement.type)}
              
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">
                  {announcement.title}
                </div>
                <div className="text-sm opacity-90 mt-1">
                  {announcement.message}
                </div>
              </div>
            </div>

            {announcement.isDismissible && (
              <button
                onClick={() => dismissAnnouncement(announcement.id)}
                className="ml-4 p-1 rounded-full hover:bg-white/20 transition-colors duration-200 flex-shrink-0"
                aria-label="Dismiss announcement"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
